import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import { LinkIcon } from '@synoptic/ui-kit/icons/react/link.js';
import { QuoteIcon } from '@synoptic/ui-kit/icons/react/quote.tsx';
import { RepostIcon } from '@synoptic/ui-kit/icons/react/repost.tsx';
import { ShareIcon } from '@synoptic/ui-kit/icons/react/share.tsx';
import {
  ModalContent,
  ModalRoot,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.js';
import { PostTagButton } from '@synoptic/ui-kit/post-tag-button/post-tag-button.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { useState } from 'react';
import {
  CollectionStreamPostFragmentFragment,
  PostCardFragmentFragment,
  StreamPostCardFragmentFragment,
  TournamentPostCardFragmentFragment,
} from '@/__generated__/graphql.ts';
import { paths } from '@/routes-utils/paths.ts';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard.ts';
import { NewPostForm } from '../../new-post-modal/form.tsx';
import { useAddPost } from '../../new-post-modal/use-add-post.tsx';
import { useRePost, useUnRePost } from '../use-repost.ts';

const isQuotablePost = (
  post:
    | PostCardFragmentFragment
    | StreamPostCardFragmentFragment
    | CollectionStreamPostFragmentFragment
    | TournamentPostCardFragmentFragment,
): post is
  | PostCardFragmentFragment
  | StreamPostCardFragmentFragment
  | TournamentPostCardFragmentFragment => {
  return post.__typename !== 'StreamPost' || 'author' in post;
};

export const PostCardShareActionsMenu = ({
  post,
  isMyRepost,
  repostId,
  quoteAllowed = false,
}: {
  post:
    | PostCardFragmentFragment
    | StreamPostCardFragmentFragment
    | CollectionStreamPostFragmentFragment
    | TournamentPostCardFragmentFragment;
  isMyRepost?: boolean;
  repostId?: string | null;
  quoteAllowed?: boolean;
}) => {
  const [showQuotePost, setShowQuotePost] = useState(false);
  const [rePost] = useRePost({ originalPostId: post.id });
  const [unRePost] = useUnRePost(repostId ?? '');
  const [addPost] = useAddPost();
  const toast = useToast();
  const canRepost = repostId !== undefined;

  const actions = [
    isMyRepost ? (
      <DropdownMenuItem
        key="undo-re-post"
        icon={<RepostIcon />}
        onSelect={() => {
          unRePost().then(() => {
            toast.info({ title: 'Post removed from your feed' });
          });
        }}
      >
        Undo Repost
      </DropdownMenuItem>
    ) : canRepost ? (
      <DropdownMenuItem
        key="re-post"
        icon={<RepostIcon />}
        onSelect={() => {
          rePost().then(() => {
            toast.info({ title: 'Post shared to your feed' });
          });
        }}
      >
        Repost
      </DropdownMenuItem>
    ) : null,

    quoteAllowed ? (
      <ModalTrigger key="quote" asChild>
        <DropdownMenuItem
          onClick={(e) => e.stopPropagation()}
          onSelect={() => setShowQuotePost(true)}
          icon={<QuoteIcon />}
        >
          Quote
        </DropdownMenuItem>
      </ModalTrigger>
    ) : null,

    <DropdownMenuItem
      key="copy-link"
      icon={<LinkIcon />}
      onSelect={() => {
        copyUrlToClipboard(paths.makePost(post.id));
        toast.info({ title: 'Link copied to clipboard' });
      }}
    >
      Copy link
    </DropdownMenuItem>,
  ];

  return (
    <ModalRoot open={showQuotePost} onOpenChange={setShowQuotePost}>
      <DropdownMenuRoot>
        <DropdownMenuTrigger>
          <PostTagButton startIcon={<ShareIcon />}>Share</PostTagButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{actions}</DropdownMenuContent>
      </DropdownMenuRoot>

      <ModalContent onClick={(e) => e.stopPropagation()}>
        <NewPostForm
          titleText={'Quote post'}
          onSubmit={async (input) => {
            await addPost(input);
            setShowQuotePost(false);
          }}
          post={isQuotablePost(post) ? post : undefined}
        />
      </ModalContent>
    </ModalRoot>
  );
};
